import React, {Children} from 'react';

import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import {TableHead as MuiTableHead} from '@mui/material';
import TableCell, {SortDirection} from '@mui/material/TableCell';
import TableSortLabel from '@mui/material/TableSortLabel';
import {useListContext} from './ListContext';
import {Identifier} from 'components/record/RecordContext';
import {isColumnHidden, useColumnVisibility} from './columnVisibility';
import ActionMenu from './actionMenu/ActionMenu';
import {stickyActionsHeaderSx} from './stickyActions';
import {isValidElement} from 'react';

type ColumnElement = React.ReactElement<{
  source?: string;
  label?: React.ReactNode;
  align?: 'center' | 'left' | 'right' | 'inherit' | 'justify';
  width?: string;
  hideOnMobile?: boolean;
}>;

// import { visuallyHidden } from './utils';

// ----------------------------------------------------------------------

const TableHead = ({order, orderBy, rowCount, children, onRequestSort, bulkOptions, ids}: TableHeadProp) => {
  const {selected, setSelected} = useListContext();
  const visibility = useColumnVisibility();
  const fields = Children.toArray(children).filter(
    (field, index): field is ColumnElement => isValidElement(field) && !(visibility && isColumnHidden(visibility.columns[index], visibility.overrides))
  );
  const onSort = (property: string) => () => {
    onRequestSort(property);
  };

  const handleSelectAllClick = (checked: boolean) => {
    if (!checked) {
      setSelected(ids);
    } else {
      setSelected([]);
    }
  };

  return (
    <MuiTableHead>
      <TableRow>
        {bulkOptions && (
          <TableCell padding="checkbox">
            <Checkbox
              indeterminate={selected.length > 0 && selected.length < rowCount}
              checked={rowCount > 0 && selected.length === rowCount}
              onChange={() => {
                handleSelectAllClick(rowCount > 0 && selected.length === rowCount);
              }}
            />
          </TableCell>
        )}

        {fields.map((field, index) => {
          if (isValidElement(field)) {
            const columnType = field.type as {listColumn?: string};
            const isActions = field.type === ActionMenu || columnType.listColumn === 'actions';
            if (field.props && 'source' in field.props && 'label' in field.props && !isActions) {
              return (
                <TableCell
                  key={index}
                  align={field.props?.align || 'left'}
                  sortDirection={orderBy === field.props.source ? order : false}
                  style={{width: field.props?.width}}
                  sx={field.props?.hideOnMobile ? {display: {xs: 'none', md: 'table-cell'}} : undefined}
                >
                  <TableSortLabel
                    hideSortIcon
                    active={orderBy === field.props.source}
                    direction={orderBy === field.props.source && order ? order : 'asc'}
                    onClick={onSort(field.props.source ?? '')}
                  >
                    {field.props.label as React.ReactNode}
                    {/* {orderBy === headCell.id ? <Box sx={{...visuallyHidden}}>{order === 'desc' ? 'sorted descending' : 'sorted ascending'}</Box> : null} */}
                  </TableSortLabel>
                </TableCell>
              );
            }
            return (
              <TableCell
                key={index}
                align={isActions ? 'center' : field?.props?.align || 'left'}
                sx={
                  isActions
                    ? stickyActionsHeaderSx
                    : field?.props?.hideOnMobile
                      ? {display: {xs: 'none', md: 'table-cell'}}
                      : {width: {xs: 56, md: field?.props?.width || 'auto'}}
                }
              >
                {field?.props.label || ''}
              </TableCell>
            );
          }
        })}
      </TableRow>
    </MuiTableHead>
  );
};

type HeadLabel = {
  id: string;
  align?: 'center' | 'left' | 'right' | 'justify' | 'inherit';
  width?: string;
  minWidth?: string;
  label?: string;
};

type TableHeadProp = {
  order: SortDirection;
  orderBy: string;
  rowCount: number;
  headLabel?: Array<HeadLabel>;
  // eslint-disable-next-line @typescript-eslint/ban-types
  onRequestSort: Function;
  bulkOptions?: boolean;
  children?: JSX.Element | JSX.Element[];
  ids: Identifier[];
};

export default TableHead;
